import React, { useEffect, useState } from 'react'
import { Card, CardContent, makeStyles } from '@material-ui/core'
import { get, sortBy } from 'lodash'
import { styled } from '@mui/material/styles'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { ReactComponent as HelperIcon } from 'src/assets/RiskReport_images/helperIcon.svg'
import { ReactComponent as HelperListIcon } from 'src/assets/RiskReport_images/helperListIcon.svg'
import { COLORS } from 'src/utils/Themes/colors'
import PieChart from 'src/components/common/charts/PieChart'
import {
  CHART_COLORS,
  CHART_HEIGHT,
  RISK_REPORTS,
  TO_LAKHS,
  TOOLTIP_CONTENT,
} from 'src/utils/constants/RiskReports'

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: COLORS.WHITE,
    color: COLORS.GREY,
    maxWidth: 240,
    fontSize: '11px',
    padding: '20px',
    boxShadow:
      '0px 6px 6px -3px rgb(0 0 0 / 20%), 0px 10px 14px 1px rgb(0 0 0 / 14%), 0px 4px 18px 3px rgb(0 0 0 / 12%)',
  },
}))

const Premium = ({ data }) => {
  const classes = useStyles()
  const [totalPremium, setTotalPremium] = useState(0)
  const [categories, setCategories] = useState(['', '', ''])
  const [otherCategories, setOtherCategories] = useState(['', ''])
  const [series, setSeries] = useState([0, 0, 0])

  const computeTotalPremium = () => {
    const d = data?.map((e) => e?.premium)?.reduce((a, b) => a + b, 0)
    if (d > 0) setTotalPremium((d / TO_LAKHS)?.toFixed(2))
  }

  const computeChartData = () => {
    const sortedArray = sortBy(data, ['premium'])?.reverse()
    const length = sortedArray?.length
    const topPremium1 = length > 0 ? sortedArray?.[0] : {}
    const topPremium2 = length > 1 ? sortedArray?.[1] : {}
    const otherPremiums = length > 2 ? sortedArray?.slice(2) : []

    const otherPremiumValues = otherPremiums
      ?.map((d) => d?.premium)
      ?.reduce((a, b) => a + b, 0)

    const chartValues = [
      get(topPremium1, 'premium', 0),
      get(topPremium2, 'premium', 0),
      otherPremiumValues,
    ]

    const CATEGORIES = [
      get(topPremium1, 'insuranceType', ''),
      get(topPremium2, 'insuranceType', ''),
      RISK_REPORTS.OTHERS,
    ]
    const OTHER_CATEGORIES = otherPremiums?.map((p) => p?.insuranceType)

    const SERIES = chartValues?.map((d) => {
      if (d > 0) return Number((d / TO_LAKHS)?.toFixed(2))
      return 0
    })
    setSeries(SERIES)
    setCategories(CATEGORIES)
    setOtherCategories(OTHER_CATEGORIES)
  }

  useEffect(() => {
    if (data) {
      computeTotalPremium()
      computeChartData()
    }

    return () => {
      setTotalPremium()
      setSeries()
      setCategories()
      setOtherCategories()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <>
      <div className={classes.root}>
        <Card className={classes.cardMainContainer}>
          <CardContent>
            <h2 className={classes.title}>
              Premium
              <p className={classes.supportText}>in Lakhs</p>
            </h2>
            <h3 className={classes.subTitle}>
              Total Premium:&nbsp;
              <span className={classes.subTitleHelper}>{totalPremium}</span>
              <span className={classes.helperIcon}>
                <HtmlTooltip
                  placement='bottom-end'
                  title={
                    <React.Fragment>
                      <div className={classes.helperListText}>
                        {TOOLTIP_CONTENT?.map((toolTipListItem) => (
                          <span key={`tooltip-${toolTipListItem?.id}`}>
                            <HelperListIcon />
                            <span className={classes.helperListContent}>
                              {toolTipListItem?.content}
                            </span>
                          </span>
                        ))}
                      </div>
                    </React.Fragment>
                  }
                >
                  <HelperIcon />
                </HtmlTooltip>
              </span>
            </h3>
            {series?.length > 0 && (
              <PieChart
                categories={categories}
                otherCategories={otherCategories}
                series={series}
                height={CHART_HEIGHT}
                colors={CHART_COLORS}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Premium

const useStyles = makeStyles({
  root: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: '10px',
    textAlign: 'center',
    // boxShadow: "4px 7px 15px rgba(0, 0, 0, 0.1)",
  },
  cardMainContainer: {
    borderRadius: '10px',
    padding: '6px',
    overflow: 'visible',
  },
  title: {
    fontSize: '18px',
    lineHeight: '22px',
    marginTop: '0px',
    color: COLORS.GREY,
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyItems: 'center',
    flexDirection: 'column',
    fontWeight: '600',
  },
  supportText: {
    color: COLORS.GREY_12,
    marginTop: 0,
    fontSize: 15,
  },
  subTitle: {
    fontSize: '15px',
    lineHeight: '18px',
    marginTop: '0px',
    color: 'rgba(51, 51, 51, 0.5)',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subTitleHelper: {
    fontSize: '22px',
    color: COLORS.GREY,
  },
  helperIcon: {
    width: '20px',
    color: COLORS.RED,
    cursor: 'pointer',
    margin: '2px 5px 0px 10px',
    '& svg': {
      maxWidth: '100%',
    },
    '& img': {
      maxWidth: '100%',
    },
  },
  helperListText: {
    fontSize: '12px',
    color: COLORS.BLACK,
    '& > span': {
      display: 'flex',
      marginBottom: '10px',
      '& > svg': {
        flexBasis: '12px',
        flexShrink: 0,
        marginRight: '14px',
        maxWidth: '12px',
      },
      '& > span': {
        lineHeight: '16px',
      },
      '&:last-child': {
        margin: 0,
      },
    },
  },
})
